import { useEffect, useRef, useState, useCallback } from 'react';

interface UseVisibleItemsOptions {
  bufferSize?: number;
  rootMargin?: string;
  threshold?: number;
}

interface UseVisibleItemsReturn {
  visibleIndices: Set<number>;
  setItemRef: (index: number) => (element: HTMLElement | null) => void;
}

export function useVisibleItems(
  totalItems: number,
  options: UseVisibleItemsOptions = {},
): UseVisibleItemsReturn {
  const { bufferSize = 3, rootMargin = '200px', threshold = 0 } = options;
  const [visibleIndices, setVisibleIndices] = useState<Set<number>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const itemRefsRef = useRef<Map<number, HTMLElement>>(new Map());
  const indexMapRef = useRef<Map<HTMLElement, number>>(new Map());

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const rootMarginValue = parseInt(rootMargin.replace('px', ''), 10) || 200;

    const observer = new IntersectionObserver(
      (entries) => {
        setVisibleIndices((prev) => {
          let hasChanges = false;
          const newSet = new Set(prev);

          entries.forEach((entry) => {
            const index = indexMapRef.current.get(entry.target as HTMLElement);

            if (index !== undefined) {
              if (entry.isIntersecting) {
                for (
                  let i = Math.max(0, index - bufferSize);
                  i <= Math.min(totalItems - 1, index + bufferSize);
                  i++
                ) {
                  if (!newSet.has(i)) {
                    newSet.add(i);
                    hasChanges = true;
                  }
                }
              } else if (entry.intersectionRatio === 0) {
                for (
                  let i = Math.max(0, index - bufferSize);
                  i <= Math.min(totalItems - 1, index + bufferSize);
                  i++
                ) {
                  if (newSet.has(i)) {
                    const element = itemRefsRef.current.get(i);
                    if (element) {
                      const rect = element.getBoundingClientRect();
                      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
                      const isFarFromViewport = rect.bottom < -rootMarginValue || rect.top > viewportHeight + rootMarginValue;
                      if (isFarFromViewport) {
                        newSet.delete(i);
                        hasChanges = true;
                      }
                    } else {
                      newSet.delete(i);
                      hasChanges = true;
                    }
                  }
                }
              }
            }
          });

          return hasChanges ? newSet : prev;
        });
      },
      {
        root: null,
        rootMargin,
        threshold,
      },
    );

    observerRef.current = observer;

    const items = Array.from(itemRefsRef.current.values());
    items.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      observer.disconnect();
    };
  }, [totalItems, bufferSize, rootMargin, threshold]);

  const setItemRef = useCallback(
    (index: number) => (element: HTMLElement | null) => {
      if (element) {
        const oldElement = itemRefsRef.current.get(index);
        if (oldElement && observerRef.current) {
          observerRef.current.unobserve(oldElement);
          indexMapRef.current.delete(oldElement);
        }
        itemRefsRef.current.set(index, element);
        indexMapRef.current.set(element, index);
        if (observerRef.current) {
          observerRef.current.observe(element);
        }
      } else {
        const elementToRemove = itemRefsRef.current.get(index);
        if (elementToRemove && observerRef.current) {
          observerRef.current.unobserve(elementToRemove);
          indexMapRef.current.delete(elementToRemove);
        }
        itemRefsRef.current.delete(index);
      }
    },
    [],
  );

  const initializedRef = useRef(false);

  useEffect(() => {
    if (totalItems > 0 && !initializedRef.current) {
      const initialIndices = new Set<number>();
      const initialCount = Math.min(totalItems, bufferSize * 2 + 1);
      for (let i = 0; i < initialCount; i++) {
        initialIndices.add(i);
      }
      setVisibleIndices(initialIndices);
      initializedRef.current = true;
    }
    if (totalItems === 0) {
      initializedRef.current = false;
    }
  }, [totalItems, bufferSize]);

  return {
    visibleIndices,
    setItemRef,
  };
}
